import { useSyncExternalStore } from "react"
import { useStaticFeature } from "hooks/use-feature"
import { useMediaBreakpoint } from "@kit/design/use-media-breakpoint"
import { toast } from "@kit/design/toast"
import { request } from "react-rb/request"
import { usePageProps, isInertiaPage } from "core/navigation"

const ERROR_TEXT = "Something went wrong. Please try again."

const OPT_IN_URL = "/vertical-navigation/opt-in"

// localStorage values. Each "show" state names the specific hint to surface
// next, rather than deriving it from whether the creator is currently on the
// vertical nav. Opting in stores the new-nav state *before* the page reloads,
// while it's still on the classic nav — a derived check would briefly show the
// classic-nav hint during that window.
export const SHOW_OLD_NAV_HINT = "show-old-nav-hint"
export const SHOW_NEW_NAV_HINT = "show-new-nav-hint"
export const SHOW_REVERT_MODAL = "show-revert-modal"
export const DONE = "done"

const storageKey = () => `vertical-nav-optin:${window.app?.user?.account_id}`

const UPGRADE_PATHS = ["/upgrade", "/account/upgrade"]
const isUpgradePath = pathname => UPGRADE_PATHS.includes(pathname)

// Back the state with a shared external store rather than per-component state:
// the modal, the old nav, and the new nav each call useVerticalNavOptIn(), and
// a choice made in one (e.g. "stay on the old nav") must update the others
// immediately — not only on the next full page load.
const listeners = new Set()

const readState = () => {
  try {
    return window.localStorage.getItem(storageKey())
  } catch {
    return null
  }
}

const writeState = value => {
  try {
    window.localStorage.setItem(storageKey(), value)
  } catch {
    // Ignore write failures (e.g. storage disabled); the experience still works
    // for this page load.
  }
  listeners.forEach(listener => listener())
}

const subscribe = listener => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Suppress the opt-in modal for a new signup: they see Kit's current
 * navigation as-is and can still opt in later from the account menu. Only
 * seeds state when none exists, so it never overrides an existing choice.
 */
export function skipVerticalNavOptInModal() {
  if (readState() == null) writeState(DONE)
}

/**
 * Drives the vertical navigation opt-in experience: the greeting modal, the
 * menu buttons to switch navs, and the guidance hints.
 *
 * State machine, persisted in localStorage and scoped to the account:
 *   null                 -> eligible for the greeting modal
 *   "show-old-nav-hint"  -> on the classic nav; hint how to switch to the new nav
 *   "show-new-nav-hint"  -> on the vertical nav; hint how to revert to the old nav
 *   "show-revert-modal"  -> back on the classic nav after reverting; show the
 *                           "you switched back" feedback modal
 *   "done"               -> nothing left to show
 */
export function useVerticalNavOptIn() {
  // The whole opt-in experience (greeting modal, guidance hints, revert
  // feedback, and the menu controls to switch navs) is desktop-only — the
  // vertical nav lives in a desktop layout — so skip it below the `md`
  // breakpoint by folding the screen check into the master eligibility flag.
  const onLargeScreen = useMediaBreakpoint("md")
  const isLoggedIn = window.app?.user?.account_id != null
  const admin = !!window.app?.user?.admin
  // Nav-less pages shouldn't show the opt-in modal
  const pageProps = usePageProps()
  const layoutConfig = isInertiaPage()
    ? pageProps?.layoutConfig
    : window.app?.layoutConfig
  // Only show on pages we can positively confirm have a visible navbar. A
  // missing config (or any missing subcomponent) is treated as nav-less, so an
  // unknown page can never accidentally surface the modal.
  const onTakeoverPage = layoutConfig?.components?.navbar?.visible !== true
  // Upgrade pages keep the nav visible, so suppress the modal there by path
  const onUpgradePage = isUpgradePath(window.location?.pathname)
  const optInEnabled =
    useStaticFeature("vertical_navigation_optin").enabled &&
    onLargeScreen &&
    isLoggedIn &&
    !onTakeoverPage &&
    !onUpgradePage
  const onVerticalNav = useStaticFeature("vertical_navigation").enabled
  const state = useSyncExternalStore(subscribe, readState, () => null)
  const setState = writeState

  const shouldShowModal =
    optInEnabled && !onVerticalNav && state == null && !admin
  const shouldShowOldNavHint =
    optInEnabled && !onVerticalNav && state === SHOW_OLD_NAV_HINT
  const shouldShowNewNavHint =
    optInEnabled && onVerticalNav && state === SHOW_NEW_NAV_HINT
  const shouldShowRevertModal =
    optInEnabled && !onVerticalNav && state === SHOW_REVERT_MODAL

  // Stay on the old nav. No reload needed — we're already there, so surface the
  // hint pointing to the switch into the new nav.
  const chooseOldNav = () => setState(SHOW_OLD_NAV_HINT)

  // Opt in to the vertical nav. Persist the new-nav state before reloading so
  // the new-nav hint shows once the page comes back on the vertical nav — and
  // the classic-nav hint never flashes during the reload.
  const enableVerticalNav = async () => {
    const { ok } = await request(OPT_IN_URL, { method: "post" })
    if (!ok) {
      toast({ type: "error", text: ERROR_TEXT })
      return
    }
    setState(SHOW_NEW_NAV_HINT)
    window.location.reload()
  }

  // Switch back to the old nav. Persist the revert-modal state before reloading
  // so the "you switched back" feedback modal shows once the page comes back on
  // the classic nav.
  const switchToOldNav = async () => {
    const { ok } = await request(OPT_IN_URL, { method: "delete" })
    if (!ok) {
      toast({ type: "error", text: ERROR_TEXT })
      return
    }
    setState(SHOW_REVERT_MODAL)
    window.location.reload()
  }

  // Resolve the revert feedback modal. Either way, leave the old-nav hint so
  // the creator still knows how to come back to the new nav.
  const acknowledgeRevert = () => {
    setState(SHOW_OLD_NAV_HINT)
  }

  // Only meaningful while a hint is pending. Read fresh state and guard against
  // clobbering the initial `null`: a creator who can't opt in yet still opens
  // these menus normally, and marking them "done" would hide the greeting modal
  // forever once the opt-in is enabled for them.
  const dismissHint = () => {
    const current = readState()
    if (current === SHOW_OLD_NAV_HINT || current === SHOW_NEW_NAV_HINT) {
      setState(DONE)
    }
  }

  return {
    optInEnabled,
    onVerticalNav,
    shouldShowModal,
    shouldShowOldNavHint,
    shouldShowNewNavHint,
    shouldShowRevertModal,
    chooseOldNav,
    enableVerticalNav,
    switchToOldNav,
    acknowledgeRevert,
    dismissHint
  }
}
